import { useCallback, useEffect, useRef, useState } from "react";

import { useMedplum } from "@medplum/react";

import * as Sentry from "@sentry/react";

import { loadPracticeLedger } from "@/billing/services/practice-ledger";
import {
  DEFAULT_PRACTICE_LEDGER_SORT,
  EMPTY_PRACTICE_LEDGER_TOTALS,
  type PracticeLedgerDateRange,
  type PracticeLedgerRow,
  type PracticeLedgerSearch,
  type PracticeLedgerSortState,
  type PracticeLedgerTotals,
  type UsePracticeLedgerTableDataResult,
} from "@/billing/types/practice-ledger";
import { toast } from "@/utils/notifications";

const PRACTICE_LEDGER_LOAD_ERROR_TOAST_ID = "practice-ledger-load-error";

/**
 * Loads one page of the practice Ledger for a Show: window. KPIs (`totals`) cover the whole window;
 * search and paging only change `rows` / `totalRows`. Returns no rows and no error while
 * `practiceId` is missing or blank, so the caller can render before the route param resolves. Switching
 * practices clears the previous practice's rows immediately. Load failures go to Sentry and a
 * toast; the caller can still branch on `error`.
 *
 * `dateRange` is depended on by timestamp so a new Date object with the same bounds does not
 * refetch. `search` and `sort` are depended on by value for the same reason.
 */
export function usePracticeLedgerTableData(
  practiceId: string | undefined,
  dateRange: PracticeLedgerDateRange,
  refreshKey = 0,
  search?: PracticeLedgerSearch,
  page = 1,
  sort: PracticeLedgerSortState = DEFAULT_PRACTICE_LEDGER_SORT,
): UsePracticeLedgerTableDataResult {
  const medplum = useMedplum();
  const [rows, setRows] = useState<PracticeLedgerRow[]>([]);
  const [totals, setTotals] = useState<PracticeLedgerTotals>(EMPTY_PRACTICE_LEDGER_TOTALS);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const latestRequestIdRef = useRef(0);
  const loadedPracticeIdRef = useRef<string | undefined>(undefined);

  const dateRangeStartTimestamp = dateRange.startDate.getTime();
  const dateRangeEndTimestamp = dateRange.endDate.getTime();
  const searchQuery = search?.query ?? "";
  const searchOption = search?.option;
  const sortColumn = sort.column;
  const sortDirection = sort.direction;

  const load = useCallback(async () => {
    if (!practiceId?.trim()) {
      latestRequestIdRef.current += 1;
      loadedPracticeIdRef.current = undefined;
      setRows([]);
      setTotals(EMPTY_PRACTICE_LEDGER_TOTALS);
      setTotalRows(0);
      setError(undefined);
      setIsLoading(false);
      return;
    }

    const requestId = ++latestRequestIdRef.current;
    if (loadedPracticeIdRef.current !== practiceId) {
      loadedPracticeIdRef.current = practiceId;
      setRows([]);
      setTotals(EMPTY_PRACTICE_LEDGER_TOTALS);
      setTotalRows(0);
    }
    setIsLoading(true);
    setError(undefined);

    const result = await loadPracticeLedger(
      medplum,
      practiceId,
      {
        startDate: new Date(dateRangeStartTimestamp),
        endDate: new Date(dateRangeEndTimestamp),
      },
      {
        search: searchOption ? { query: searchQuery, option: searchOption } : undefined,
        page,
        sort: { column: sortColumn, direction: sortDirection },
      },
    );
    if (requestId !== latestRequestIdRef.current) return;

    if (result.ok) {
      setRows(result.data.rows);
      setTotals(result.data.totals);
      setTotalRows(result.data.totalRows);
    } else {
      const loadError = new Error(`Practice Ledger load failed: ${result.reason}`);
      setRows([]);
      setTotals(EMPTY_PRACTICE_LEDGER_TOTALS);
      setTotalRows(0);
      setError(loadError);
      Sentry.captureException(loadError, { extra: { details: result.details } });
      toast({
        id: PRACTICE_LEDGER_LOAD_ERROR_TOAST_ID,
        title: "Could not load ledger",
        message: "Please try again. If the error persists, contact support.",
      });
    }
    setIsLoading(false);
  }, [
    dateRangeEndTimestamp,
    dateRangeStartTimestamp,
    medplum,
    page,
    practiceId,
    searchOption,
    searchQuery,
    sortColumn,
    sortDirection,
  ]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  return { rows, totals, totalRows, isLoading, error };
}
